import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { isHrFamilyRole } from "../../utils/hrStaffHelpers";
import {
  acceptOffer,
  approveOffer,
  approveRequisition,
  cancelRequisition,
  createCandidate,
  createInterview,
  createOffer,
  createPosting,
  createRequisition,
  declineOffer,
  hireApplication,
  listApplications,
  listCandidates,
  listInterviews,
  listOffers,
  listPostings,
  listRequisitions,
  rejectOffer,
  rejectRequisition,
  submitInterviewFeedback,
  screenApplication,
  updateApplicationStage,
  updatePosting,
} from "../../api/recruitment";
import { uploadAttachment } from "../../api/attachments";
import {
  maritalStatusLabel,
  militaryStatusLabel,
} from "../../utils/recruitmentApplyOptions";
import {
  canActOnRequisition,
  isOwnRequisition,
  isOpenRequisition,
  requisitionCreatorName,
  requisitionNextApproverLabel,
  requisitionStatusLabel,
} from "../../utils/recruitmentRequisition";

const STAGES = [
  { key: "NEW", label: "جديد" },
  { key: "SCREENING", label: "فرز" },
  { key: "INTERVIEW", label: "مقابلة" },
  { key: "OFFER", label: "عرض" },
  { key: "HIRED", label: "تعيين" },
  { key: "REJECTED", label: "مرفوض" },
  { key: "WITHDRAWN", label: "منسحب" },
];

function apiError(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function unwrap(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function sourceLabel(source) {
  if (source === "PUBLIC") return "بوابة خارجية";
  if (source === "INTERNAL") return "موظف داخلي";
  if (source === "REFERRAL") return "ترشيح";
  return "إدخال HR";
}

function postingAudienceLabel(internalOnly) {
  return internalOnly === false ? "بوابة خارجية" : "داخلي فقط";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("ar-EG");
}

function screeningMissingFields(app) {
  const raw = app?.screeningMissing;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.fields)) return raw.fields;
  return [];
}

function screeningStatusLabel(status) {
  if (status === "PENDING") return "جارٍ التحليل...";
  if (status === "COMPLETED") return "تم التحليل";
  if (status === "FAILED") return "فشل التحليل";
  if (status === "SKIPPED") return "AI غير مفعّل";
  return "";
}

function matchScoreClass(score) {
  if (score == null) return "";
  if (score >= 75) return "hr-recruitment__score--high";
  if (score >= 50) return "hr-recruitment__score--mid";
  return "hr-recruitment__score--low";
}

export default function HrRecruitment({ mode = "hr" }) {
  const { user } = useAuth();
  const { departments, refreshData } = useData();
  const allEmployees = Object.values(useData().employeesByBranch || {}).flat();
  const [tab, setTab] = useState(mode === "gm" ? "requisitions" : "pipeline");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [requisitions, setRequisitions] = useState([]);
  const [postings, setPostings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [screeningId, setScreeningId] = useState(null);

  const [reqForm, setReqForm] = useState({
    title: "",
    departmentId: "",
    reason: "",
    headcount: 1,
  });
  const [candForm, setCandForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    postingId: "",
    cvFile: null,
  });
  const [hireForm, setHireForm] = useState({
    applicationId: "",
    password: "",
  });
  const [interviewForm, setInterviewForm] = useState({
    applicationId: "",
    scheduledAt: "",
    interviewerEmployeeIds: "",
  });
  const [offerForm, setOfferForm] = useState({
    applicationId: "",
    salary: "",
    startDate: "",
  });

  const canManage = isHrFamilyRole(user?.role) || user?.role === "admin";
  const canHire = user?.role === "hr" || user?.role === "gm" || user?.role === "admin";
  const canApprove = user?.role === "gm" || user?.role === "hr" || user?.role === "hr-staff" || user?.role === "admin" || user?.role === "department-manager";

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [req, posts, apps, cands, ints, offs] = await Promise.allSettled([
        listRequisitions(),
        listPostings(),
        listApplications(),
        listCandidates().catch(() => []),
        listInterviews().catch(() => []),
        listOffers().catch(() => []),
      ]);
      setRequisitions(req.status === "fulfilled" ? unwrap(req.value) : []);
      setPostings(posts.status === "fulfilled" ? unwrap(posts.value) : []);
      setApplications(apps.status === "fulfilled" ? unwrap(apps.value) : []);
      setCandidates(cands.status === "fulfilled" ? unwrap(cands.value) : []);
      setInterviews(ints.status === "fulfilled" ? unwrap(ints.value) : []);
      setOffers(offs.status === "fulfilled" ? unwrap(offs.value) : []);
    } catch (err) {
      setError(apiError(err, "تعذر تحميل بيانات التوظيف"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const deptOptions = useMemo(
    () =>
      (departments || [])
        .filter((d) => d.isActive !== false && (d.backendId || d.id))
        .map((d) => ({ id: d.backendId || d.id, name: d.name })),
    [departments],
  );

  const handleCreateRequisition = async (e) => {
    e.preventDefault();
    try {
      await createRequisition({
        title: reqForm.title,
        departmentId: reqForm.departmentId,
        reason: reqForm.reason,
        headcount: Number(reqForm.headcount) || 1,
      });
      setReqForm({ title: "", departmentId: "", reason: "", headcount: 1 });
      await load();
    } catch (err) {
      setError(apiError(err, "تعذر تقديم طلب الشاغر"));
    }
  };

  const handleCreateCandidate = async (e) => {
    e.preventDefault();
    try {
      const created = await createCandidate({
        fullName: candForm.fullName,
        phone: candForm.phone || undefined,
        email: candForm.email || undefined,
        postingId: candForm.postingId || undefined,
      });
      if (candForm.cvFile && created?.id) {
        await uploadAttachment("candidate", created.id, candForm.cvFile);
      }
      setCandForm({ fullName: "", phone: "", email: "", postingId: "", cvFile: null });
      await load();
    } catch (err) {
      setError(apiError(err, "تعذر إضافة المتقدم"));
    }
  };

  const tabs = [
    { id: "pipeline", label: "البايبلاين" },
    { id: "requisitions", label: "الشواغر" },
    { id: "postings", label: "الإعلانات" },
    { id: "candidates", label: "المتقدمين" },
    { id: "interviews", label: "المقابلات" },
    { id: "offers", label: "العروض" },
  ];

  return (
    <div className="hr-recruitment">
      <div className="hr-recruitment__header">
        <div>
          <h1 className="hr-recruitment__title">التوظيف</h1>
          <p className="hr-recruitment__subtitle">
            طلبات الشواغر، البايبلاين، المقابلات، العروض، والتعيين
          </p>
        </div>
        <button className="hr-recruitment__btn hr-recruitment__btn--ghost" onClick={load} type="button">
          تحديث
        </button>
      </div>
      {error ? <div className="hr-recruitment__error">{error}</div> : null}
      {loading ? <div className="hr-recruitment__muted">جاري التحميل...</div> : null}

      <div className="hr-recruitment__tabs">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`hr-recruitment__tab${tab === item.id ? " is-active" : ""}`}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "requisitions" && (
        <div className="hr-recruitment__panel">
          {canManage || user?.role === "department-manager" || user?.role === "manager" ? (
            <form className="hr-recruitment__form" onSubmit={handleCreateRequisition}>
              <input
                placeholder="المسمى الوظيفي"
                value={reqForm.title}
                onChange={(e) => setReqForm({ ...reqForm, title: e.target.value })}
                required
              />
              <select
                value={reqForm.departmentId}
                onChange={(e) => setReqForm({ ...reqForm, departmentId: e.target.value })}
                required
              >
                <option value="">الإدارة</option>
                {deptOptions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <input
                type="number"
                min="1"
                placeholder="العدد"
                value={reqForm.headcount}
                onChange={(e) => setReqForm({ ...reqForm, headcount: e.target.value })}
              />
              <textarea
                placeholder="سبب الطلب"
                value={reqForm.reason}
                onChange={(e) => setReqForm({ ...reqForm, reason: e.target.value })}
                required
              />
              <div className="hr-recruitment__actions">
                <button className="hr-recruitment__btn" type="submit">تقديم طلب شاغر</button>
              </div>
            </form>
          ) : null}
          <table className="hr-recruitment__table">
            <thead>
              <tr>
                <th>المسمى</th>
                <th>الإدارة</th>
                <th>مقدم الطلب</th>
                <th>الموافق التالي</th>
                <th>الحالة</th>
                <th>إجراء</th>
              </tr>
            </thead>
            <tbody>
              {requisitions.map((row) => (
                <tr key={row.id}>
                  <td>{row.title}</td>
                  <td>{row.department?.name || "—"}</td>
                  <td>{requisitionCreatorName(row)}</td>
                  <td>{requisitionNextApproverLabel(row)}</td>
                  <td>{requisitionStatusLabel(row.status || row.request?.status)}</td>
                  <td>
                    <div className="hr-recruitment__actions">
                      {isOwnRequisition(row, user) && isOpenRequisition(row) ? (
                        <button type="button" className="hr-recruitment__btn hr-recruitment__btn--ghost" onClick={async () => {
                          if (!window.confirm("هل تريد إلغاء طلب الشاغر؟ يمكنك تقديم طلب جديد بعد الإلغاء.")) return;
                          try {
                            await cancelRequisition(row.id);
                            await load();
                          } catch (err) {
                            setError(apiError(err, "تعذر إلغاء الطلب"));
                          }
                        }}>إلغاء الطلب</button>
                      ) : null}
                      {canApprove && canActOnRequisition(row, user) && (
                        <>
                          <button type="button" className="hr-recruitment__btn" onClick={async () => {
                            try {
                              await approveRequisition(row.id, {});
                              await load();
                            } catch (err) {
                              setError(apiError(err, "تعذر الاعتماد"));
                            }
                          }}>موافقة</button>
                          <button type="button" className="hr-recruitment__btn hr-recruitment__btn--danger" onClick={async () => {
                            try {
                              await rejectRequisition(row.id, { notes: "مرفوض" });
                              await load();
                            } catch (err) {
                              setError(apiError(err, "تعذر الرفض"));
                            }
                          }}>رفض</button>
                        </>
                      )}
                      {canManage && row.status === "APPROVED" && (
                        <>
                          <button type="button" className="hr-recruitment__btn hr-recruitment__btn--ghost" onClick={async () => {
                            try {
                              await createPosting({ requisitionId: row.id, internalOnly: true });
                              await load();
                            } catch (err) {
                              setError(apiError(err, "تعذر نشر الإعلان"));
                            }
                          }}>نشر داخلي</button>
                          <button type="button" className="hr-recruitment__btn" onClick={async () => {
                            try {
                              await createPosting({ requisitionId: row.id, internalOnly: false });
                              await load();
                            } catch (err) {
                              setError(apiError(err, "تعذر نشر الإعلان"));
                            }
                          }}>نشر على البوابة</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "postings" && (
        <div className="hr-recruitment__panel">
          <p className="hr-recruitment__muted">
            رابط البوابة الخارجية: {`${window.location.origin}/careers`}
          </p>
          <table className="hr-recruitment__table">
            <thead>
              <tr>
                <th>الوظيفة</th>
                <th>الحالة</th>
                <th>الظهور</th>
                <th>المتقدمين</th>
                <th>إجراء</th>
              </tr>
            </thead>
            <tbody>
              {postings.map((row) => (
                <tr key={row.id}>
                  <td>{row.title}</td>
                  <td>{row.status}</td>
                  <td>{postingAudienceLabel(row.internalOnly)}</td>
                  <td>{row._count?.applications ?? "—"}</td>
                  <td>
                    <div className="hr-recruitment__actions">
                      {canManage && row.status === "OPEN" && (
                        <>
                          <button type="button" className="hr-recruitment__btn hr-recruitment__btn--ghost" onClick={async () => {
                            try {
                              await updatePosting(row.id, { internalOnly: !row.internalOnly });
                              await load();
                            } catch (err) {
                              setError(apiError(err, "تعذر تحديث الظهور"));
                            }
                          }}>
                            {row.internalOnly === false ? "اجعلها داخلية" : "افتح للبوابة"}
                          </button>
                          <button type="button" className="hr-recruitment__btn hr-recruitment__btn--ghost" onClick={async () => {
                            try {
                              await updatePosting(row.id, { status: "CLOSED" });
                              await load();
                            } catch (err) {
                              setError(apiError(err, "تعذر إغلاق الإعلان"));
                            }
                          }}>إغلاق</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "pipeline" && (
        <div className="hr-recruitment__panel">
          <div className="hr-recruitment__kanban">
            {STAGES.filter((s) => s.key !== "WITHDRAWN").map((stage) => (
              <div key={stage.key} className="hr-recruitment__column">
                <h3>{stage.label}</h3>
                {applications.filter((a) => a.stage === stage.key).map((app) => (
                  <div key={app.id} className="hr-recruitment__card">
                    <strong>{app.candidate?.fullName || "متقدم"}</strong>
                    <div className="hr-recruitment__muted">{app.posting?.title}</div>
                    {app.candidate?.phone ? (
                      <div className="hr-recruitment__muted">{app.candidate.phone}</div>
                    ) : null}
                    {app.candidate?.education ? (
                      <div className="hr-recruitment__muted">{app.candidate.education}</div>
                    ) : null}
                    {app.candidate?.source ? (
                      <div className="hr-recruitment__muted">{sourceLabel(app.candidate.source)}</div>
                    ) : null}
                    {app.screeningStatus === "PENDING" || screeningId === app.id ? (
                      <div className="hr-recruitment__screening hr-recruitment__screening--pending">
                        {screeningId === app.id ? "جارٍ التحليل بالذكاء الاصطناعي..." : screeningStatusLabel(app.screeningStatus)}
                      </div>
                    ) : null}
                    {app.screeningScore != null && app.screeningStatus === "COMPLETED" ? (
                      <div className={`hr-recruitment__score ${matchScoreClass(app.screeningScore)}`}>
                        ملاءمة الوظيفة: {app.screeningScore}%
                      </div>
                    ) : null}
                    {app.screeningSummary && app.screeningStatus === "COMPLETED" ? (
                      <div className="hr-recruitment__screening-summary">{app.screeningSummary}</div>
                    ) : null}
                    {screeningMissingFields(app).length > 0 && app.screeningStatus === "COMPLETED" ? (
                      <div className="hr-recruitment__missing">
                        <span>مطلوب من المتقدم:</span>
                        <ul>
                          {screeningMissingFields(app).map((field) => (
                            <li key={field}>{field}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {app.screeningStatus === "FAILED" ? (
                      <div className="hr-recruitment__screening hr-recruitment__screening--failed">
                        {app.screeningSummary || "فشل تحليل السيرة الذاتية"}
                      </div>
                    ) : null}
                    {app.screeningStatus === "SKIPPED" ? (
                      <div className="hr-recruitment__muted">{app.screeningSummary || "الذكاء الاصطناعي غير مفعّل"}</div>
                    ) : null}
                    {canManage ? (
                      <div className="hr-recruitment__actions" style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          className="hr-recruitment__btn hr-recruitment__btn--ghost"
                          disabled={screeningId === app.id}
                          onClick={async () => {
                            setScreeningId(app.id);
                            setError("");
                            try {
                              await screenApplication(app.id);
                              await load();
                            } catch (err) {
                              setError(apiError(err, "تعذر تحليل السيرة الذاتية"));
                            } finally {
                              setScreeningId(null);
                            }
                          }}
                        >
                          {app.screeningStatus === "COMPLETED" ? "إعادة التحليل AI" : "تحليل AI"}
                        </button>
                      </div>
                    ) : null}
                    {canManage && stage.key !== "HIRED" && stage.key !== "REJECTED" && (
                      <div className="hr-recruitment__actions" style={{ marginTop: 8 }}>
                        {STAGES.find((s) => STAGES[STAGES.findIndex((x) => x.key === stage.key) + 1])?.key && stage.key !== "OFFER" && (
                          <button type="button" className="hr-recruitment__btn" onClick={async () => {
                            const idx = STAGES.findIndex((s) => s.key === stage.key);
                            const next = STAGES[idx + 1]?.key;
                            if (!next || next === "REJECTED") return;
                            try {
                              await updateApplicationStage(app.id, { stage: next });
                              await load();
                            } catch (err) {
                              setError(apiError(err, "تعذر نقل المرحلة"));
                            }
                          }}>التالي</button>
                        )}
                        <button type="button" className="hr-recruitment__btn hr-recruitment__btn--danger" onClick={async () => {
                          try {
                            await updateApplicationStage(app.id, { stage: "REJECTED" });
                            await load();
                          } catch (err) {
                            setError(apiError(err, "تعذر الرفض"));
                          }
                        }}>رفض</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "candidates" && (
        <div className="hr-recruitment__panel">
          {canManage && (
            <form className="hr-recruitment__form" onSubmit={handleCreateCandidate}>
              <input placeholder="اسم المتقدم" value={candForm.fullName} onChange={(e) => setCandForm({ ...candForm, fullName: e.target.value })} required />
              <input placeholder="الهاتف" value={candForm.phone} onChange={(e) => setCandForm({ ...candForm, phone: e.target.value })} />
              <input placeholder="البريد" value={candForm.email} onChange={(e) => setCandForm({ ...candForm, email: e.target.value })} />
              <select value={candForm.postingId} onChange={(e) => setCandForm({ ...candForm, postingId: e.target.value })}>
                <option value="">ربط بإعلان (اختياري)</option>
                {postings.filter((p) => p.status === "OPEN").map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => setCandForm({ ...candForm, cvFile: e.target.files?.[0] || null })}
              />
              <div className="hr-recruitment__actions">
                <button className="hr-recruitment__btn" type="submit">إضافة متقدم</button>
              </div>
            </form>
          )}
          <table className="hr-recruitment__table">
            <thead>
              <tr>
                <th>الاسم</th>
                <th>الهاتف</th>
                <th>المؤهل</th>
                <th>تاريخ الميلاد</th>
                <th>الحالة الاجتماعية</th>
                <th>التجنيد</th>
                <th>المصدر</th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr key={c.id}>
                  <td>{c.fullName}</td>
                  <td>{c.phone || "—"}</td>
                  <td>{c.education || "—"}</td>
                  <td>{formatDate(c.dateOfBirth)}</td>
                  <td>{maritalStatusLabel(c.maritalStatus)}</td>
                  <td>{militaryStatusLabel(c.militaryStatus)}</td>
                  <td>{sourceLabel(c.source)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "interviews" && (
        <div className="hr-recruitment__panel">
          {canManage && (
            <form className="hr-recruitment__form" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createInterview({
                  applicationId: interviewForm.applicationId,
                  scheduledAt: new Date(interviewForm.scheduledAt).toISOString(),
                  interviewerEmployeeIds: interviewForm.interviewerEmployeeIds.split(",").map((s) => s.trim()).filter(Boolean),
                });
                setInterviewForm({ applicationId: "", scheduledAt: "", interviewerEmployeeIds: "" });
                await load();
              } catch (err) {
                setError(apiError(err, "تعذر جدولة المقابلة"));
              }
            }}>
              <select value={interviewForm.applicationId} onChange={(e) => setInterviewForm({ ...interviewForm, applicationId: e.target.value })} required>
                <option value="">التقديم</option>
                {applications.filter((a) => a.stage !== "HIRED" && a.stage !== "REJECTED").map((a) => (
                  <option key={a.id} value={a.id}>{a.candidate?.fullName} — {a.posting?.title}</option>
                ))}
              </select>
              <input type="datetime-local" value={interviewForm.scheduledAt} onChange={(e) => setInterviewForm({ ...interviewForm, scheduledAt: e.target.value })} required />
              <select value={interviewForm.interviewerEmployeeIds} onChange={(e) => setInterviewForm({ ...interviewForm, interviewerEmployeeIds: e.target.value })}>
                <option value="">المحاور</option>
                {allEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.name || emp.fullName}</option>
                ))}
              </select>
              <div className="hr-recruitment__actions">
                <button className="hr-recruitment__btn" type="submit">جدولة مقابلة</button>
              </div>
            </form>
          )}
          <table className="hr-recruitment__table">
            <thead>
              <tr><th>المتقدم</th><th>الموعد</th><th>الحالة</th><th>تقييم</th></tr>
            </thead>
            <tbody>
              {interviews.map((row) => (
                <tr key={row.id}>
                  <td>{row.application?.candidate?.fullName}</td>
                  <td>{row.scheduledAt ? new Date(row.scheduledAt).toLocaleString("ar-EG") : "—"}</td>
                  <td>{row.status}</td>
                  <td>
                    <button type="button" className="hr-recruitment__btn hr-recruitment__btn--ghost" onClick={async () => {
                      const score = window.prompt("الدرجة من 100", row.score || "80");
                      if (score == null) return;
                      try {
                        await submitInterviewFeedback(row.id, { score: Number(score), status: "COMPLETED", outcome: "PASS" });
                        await load();
                      } catch (err) {
                        setError(apiError(err, "تعذر حفظ التقييم"));
                      }
                    }}>تقييم</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "offers" && (
        <div className="hr-recruitment__panel">
          {canManage && (
            <form className="hr-recruitment__form" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await createOffer({
                  applicationId: offerForm.applicationId,
                  salary: Number(offerForm.salary),
                  startDate: offerForm.startDate,
                });
                setOfferForm({ applicationId: "", salary: "", startDate: "" });
                await load();
              } catch (err) {
                setError(apiError(err, "تعذر إنشاء العرض"));
              }
            }}>
              <select value={offerForm.applicationId} onChange={(e) => setOfferForm({ ...offerForm, applicationId: e.target.value })} required>
                <option value="">التقديم</option>
                {applications.filter((a) => a.stage === "INTERVIEW" || a.stage === "OFFER").map((a) => (
                  <option key={a.id} value={a.id}>{a.candidate?.fullName} — {a.posting?.title}</option>
                ))}
              </select>
              <input type="number" placeholder="الراتب" value={offerForm.salary} onChange={(e) => setOfferForm({ ...offerForm, salary: e.target.value })} required />
              <input type="date" value={offerForm.startDate} onChange={(e) => setOfferForm({ ...offerForm, startDate: e.target.value })} required />
              <div className="hr-recruitment__actions">
                <button className="hr-recruitment__btn" type="submit">إنشاء عرض</button>
              </div>
            </form>
          )}
          {canHire && (
            <form className="hr-recruitment__form" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await hireApplication(hireForm.applicationId, { password: hireForm.password, roleSlug: "employee" });
                setHireForm({ applicationId: "", password: "" });
                await load();
                if (refreshData) await refreshData();
              } catch (err) {
                setError(apiError(err, "تعذر التعيين"));
              }
            }}>
              <select value={hireForm.applicationId} onChange={(e) => setHireForm({ ...hireForm, applicationId: e.target.value })} required>
                <option value="">تعيين متقدم لديه عرض معتمد</option>
                {applications.filter((a) => a.stage === "OFFER").map((a) => (
                  <option key={a.id} value={a.id}>{a.candidate?.fullName}</option>
                ))}
              </select>
              <input type="password" placeholder="كلمة مرور حساب الموظف" value={hireForm.password} onChange={(e) => setHireForm({ ...hireForm, password: e.target.value })} required />
              <div className="hr-recruitment__actions">
                <button className="hr-recruitment__btn" type="submit">تحويل لموظف</button>
              </div>
            </form>
          )}
          <table className="hr-recruitment__table">
            <thead>
              <tr><th>المتقدم</th><th>الراتب</th><th>الحالة</th><th>إجراء</th></tr>
            </thead>
            <tbody>
              {offers.map((row) => (
                <tr key={row.id}>
                  <td>{row.application?.candidate?.fullName}</td>
                  <td>{row.salary}</td>
                  <td>{row.status}</td>
                  <td>
                    <div className="hr-recruitment__actions">
                      {(user?.role === "gm" || user?.role === "admin") && row.status === "PENDING_APPROVAL" && (
                        <>
                          <button type="button" className="hr-recruitment__btn" onClick={async () => {
                            try { await approveOffer(row.id, {}); await load(); } catch (err) { setError(apiError(err, "تعذر الاعتماد")); }
                          }}>اعتماد</button>
                          <button type="button" className="hr-recruitment__btn hr-recruitment__btn--danger" onClick={async () => {
                            try { await rejectOffer(row.id, { notes: "مرفوض" }); await load(); } catch (err) { setError(apiError(err, "تعذر الرفض")); }
                          }}>رفض</button>
                        </>
                      )}
                      {canManage && (row.status === "APPROVED" || row.status === "SENT") && (
                        <>
                          <button type="button" className="hr-recruitment__btn" onClick={async () => {
                            try { await acceptOffer(row.id, {}); await load(); } catch (err) { setError(apiError(err, "تعذر التسجيل")); }
                          }}>قبول المتقدم</button>
                          <button type="button" className="hr-recruitment__btn hr-recruitment__btn--ghost" onClick={async () => {
                            try { await declineOffer(row.id, {}); await load(); } catch (err) { setError(apiError(err, "تعذر التسجيل")); }
                          }}>رفض المتقدم</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
